import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { AlarmClock } from 'lucide-react';
function getInitialTarget(): number {
  if (typeof window === 'undefined') return Date.now() + 24 * 60 * 60 * 1000;
  const stored = window.localStorage.getItem('mp_countdown_target');
  if (stored) {
    const parsed = parseInt(stored, 10);
    if (!isNaN(parsed) && parsed > Date.now()) return parsed;
  }
  const target = Date.now() + 24 * 60 * 60 * 1000;
  try {
    window.localStorage.setItem('mp_countdown_target', target.toString());
  } catch {}
  return target;
}
function pad(n: number) {
  return n.toString().padStart(2, '0');
}
export function CountdownTimer() {
  const [target, setTarget] = useState<number | null>(null);
  const [remaining, setRemaining] = useState(24 * 60 * 60 * 1000);
  useEffect(() => {
    const t = getInitialTarget();
    setTarget(t);
  }, []);
  useEffect(() => {
    if (!target) return;
    const tick = () => {
      const diff = target - Date.now();
      if (diff <= 0) {
        const next = Date.now() + 24 * 60 * 60 * 1000;
        try {
          window.localStorage.setItem('mp_countdown_target', next.toString());
        } catch {}
        setTarget(next);
        setRemaining(24 * 60 * 60 * 1000);
      } else {
        setRemaining(diff);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [target]);
  const totalHours = Math.floor(remaining / (1000 * 60 * 60));
  const minutes = Math.floor(remaining / (1000 * 60) % 60);
  const seconds = Math.floor(remaining / 1000 % 60);
  const elapsed = 24 * 60 * 60 * 1000 - remaining;
  const progress = Math.min(90, 65 + elapsed / (24 * 60 * 60 * 1000) * 25);
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} className="mt-10 max-w-3xl mx-auto bg-gradient-to-b from-navy-light to-navy-dark border border-gold/40 rounded-2xl p-6 md:p-8 shadow-[0_0_30px_rgba(212,175,55,0.15)]" data-id="element-47">
      <div className="text-center" data-id="element-48">
        <div className="flex items-center justify-center gap-2 text-gold font-bold tracking-wider uppercase text-sm md:text-base mb-6" data-id="element-49">
          <AlarmClock size={20} className="animate-pulse" data-id="element-50" />
          <span data-id="element-51">Launch pricing ends in:</span>
        </div>

        <div className="flex items-center justify-center gap-3 md:gap-5 mb-5" data-id="element-52">
          <TimeBox value={pad(totalHours)} label="hrs" data-id="element-53" />
          <span className="text-3xl md:text-5xl font-bold text-gold" data-id="element-54">:</span>
          <TimeBox value={pad(minutes)} label="min" data-id="element-55" />
          <span className="text-3xl md:text-5xl font-bold text-gold" data-id="element-56">:</span>
          <TimeBox value={pad(seconds)} label="sec" data-id="element-57" />
        </div>

        <p className="text-xs md:text-sm font-bold tracking-wider uppercase text-white/80 mb-8" data-id="element-58">
          After this, the price goes up and does not come back down.
        </p>

        <div className="space-y-2" data-id="element-59">
          <div className="flex justify-between text-[11px] md:text-xs font-bold uppercase tracking-wider" data-id="element-60">
            <span className="text-gold-light" data-id="element-61">
              Launch pricing spots remaining
            </span>
            <span className="text-gold" data-id="element-62">Only a few left</span>
          </div>
          <div className="w-full h-2.5 bg-navy-dark/80 rounded-full overflow-hidden border border-gold/30" data-id="element-63">
            <motion.div initial={{
            width: 0
          }} whileInView={{
            width: `${progress}%`
          }} viewport={{
            once: true
          }} transition={{
            duration: 1.5,
            ease: 'easeOut'
          }} className="h-full bg-gold-gradient rounded-full shadow-[0_0_10px_rgba(212,175,55,0.6)]" data-id="element-64" />
          </div>
          <p className="text-right text-[11px] md:text-xs text-gold-light font-medium" data-id="element-65">
            {Math.round(progress)}% of spots taken
          </p>
        </div>
      </div>
    </motion.div>;
}
function TimeBox({
  value,
  label
}: {
  value: string;
  label: string;
}) {
  return <div className="bg-navy-dark border-2 border-gold/40 rounded-xl px-4 md:px-6 py-3 md:py-4 min-w-[80px] md:min-w-[110px] shadow-[inset_0_0_15px_rgba(212,175,55,0.1)]" data-id="element-66">
      <div className="flex items-baseline justify-center gap-1" data-id="element-67">
        <span className="text-4xl md:text-5xl font-bold text-white tabular-nums" data-id="element-68">
          {value}
        </span>
        <span className="text-xs md:text-sm font-medium text-gold-light" data-id="element-69">
          {label}
        </span>
      </div>
    </div>;
}